import { Card, CardBody, Loader, Table } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  alignmentNotesAtom,
  playbackAndMinutesOkAtom,
  draftIsAlignedAtom,
} from '../../atoms/videoAlignmentCheck';
import {
  removeVideoFromVideosRequiringAlignmentCheckAtom,
  updateVideoInfoInVideosRequiringAlignmentCheckAtom,
  videoReferenceSelectedForAlignmentCheckAtom,
  videosRequiringAlignmentCheckAtom,
} from '../../atoms/videoAlignmentRequired';
import CardHeader from '../../components/CardHeader';
import { CellHeadings } from '../../components/CellHeadings/CellHeadings';
import VideoFilters from '../../components/VideoFilters';
import TableRows from '../../components/MisalignedEventsTableRows';
import VideoAlignmentCheck from '../../components/VideoAlignmentCheck';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import convertKeys from '../../utils/convertKeys';
import { isNotNullOrUndefined } from '../../utils/funcs';
import { filterVideos } from '../../utils/videoEvents';
import getAlignmentIssueHeadingCells from './AlignmentIssues.helper';
import { getAlignmentOutcome } from '../../utils/alignment-data';

export function AlignmentIssues() {
  const intl = useIntl();

  const [videosRequiringAlignmentCheck, setVideosRequiringAlignmentCheck] =
    useAtom(videosRequiringAlignmentCheckAtom);
  const [videoSelectedForAlignmentCheck, setVideoSelectedForAlignmentCheck] =
    useAtom(videoReferenceSelectedForAlignmentCheckAtom);
  const removeCurrentMisalignedEvent = useSetAtom(
    removeVideoFromVideosRequiringAlignmentCheckAtom,
  );
  const updateCurrentMisalignedEvent = useSetAtom(
    updateVideoInfoInVideosRequiringAlignmentCheckAtom,
  );

  const isAligned = useAtomValue(draftIsAlignedAtom);
  const playbackAndMinutesOk = useAtomValue(playbackAndMinutesOkAtom);
  const alignmentNotes = useAtomValue(alignmentNotesAtom);
  const { get, errorMessage, isFetching } = useApi();

  const [competitions, setCompetitions] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [alignmentIssue, setAlignmentIssue] = useState(undefined);
  const [alignmentStatus, setAlignmentStatus] = useState(undefined);
  const [matchId, setMatchId] = useState('');

  const handleModalFinalConfirm = () => {
    // Update local state to reflect server state without re-fetching
    if (isAligned) {
      // Event is aligned, so remove it from misaligned events
      removeCurrentMisalignedEvent(videoSelectedForAlignmentCheck);
    } else {
      // Event is not aligned, update with the correct issue, status and notes
      updateCurrentMisalignedEvent(videoSelectedForAlignmentCheck, {
        ...(isNotNullOrUndefined(playbackAndMinutesOk) &&
          getAlignmentOutcome(playbackAndMinutesOk)),
        alignmentNotes,
      });
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      const response = await get(
        `${API_ENDPOINTS.videoService}/streams/misaligned`,
      );

      setVideosRequiringAlignmentCheck(convertKeys(response?.data));
    };

    fetchEvents();
  }, []);

  const handleEdit = (videoReference) => {
    setVideoSelectedForAlignmentCheck(videoReference);
  };

  const headingCells = useMemo(
    () => getAlignmentIssueHeadingCells(intl),
    [intl],
  );

  const filteredVideosRequiringAlignmentCheck = useMemo(
    () =>
      filterVideos(
        videosRequiringAlignmentCheck,
        competitions,
        seasons,
        alignmentIssue?.value,
        alignmentStatus?.value,
        matchId,
      ),
    [
      videosRequiringAlignmentCheck,
      competitions,
      seasons,
      alignmentIssue,
      alignmentStatus,
      matchId,
    ],
  );

  if (isFetching) {
    return <Loader />;
  }

  if (errorMessage) {
    return (
      <div>
        {intl.formatMessage({
          defaultMessage: 'Error getting events',
          id: 'e5c1c',
        })}
        : {errorMessage}
      </div>
    );
  }

  if (
    videosRequiringAlignmentCheck &&
    videosRequiringAlignmentCheck.length < 1
  ) {
    return (
      <div>
        {intl.formatMessage({
          defaultMessage: 'No events are misaligned',
          id: '33f86',
        })}
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <FormattedMessage defaultMessage="Alignment Issues" id="6cab4" />
        </CardHeader>
        <VideoFilters
          competitions={competitions}
          seasons={seasons}
          alignmentIssue={alignmentIssue}
          alignmentStatus={alignmentStatus}
          matchId={matchId}
          setCompetitions={setCompetitions}
          setSeasons={setSeasons}
          setAlignmentIssue={setAlignmentIssue}
          setAlignmentStatus={setAlignmentStatus}
          setMatchId={setMatchId}
          videos={videosRequiringAlignmentCheck}
        />
        <CardBody>
          <Table>
            <CellHeadings cells={getAlignmentIssueHeadingCells(intl)} />
            <Table.Body>
              {filteredVideosRequiringAlignmentCheck.length > 0 ? (
                <TableRows
                  misalignedEvents={filteredVideosRequiringAlignmentCheck}
                  handleEdit={handleEdit}
                />
              ) : (
                <Table.Row>
                  <Table.DataCell colSpan={headingCells.length}>
                    <FormattedMessage
                      defaultMessage="No misaligned events found"
                      id="43567"
                    />
                  </Table.DataCell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </CardBody>
      </Card>
      <VideoAlignmentCheck onFinalConfirm={handleModalFinalConfirm} />
    </>
  );
}

export default { AlignmentIssues };
